import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { User as UserIcon, MoreHorizontal } from "lucide-react";

/**
 * Public comments block under a Material page.
 * - one-level threading
 * - authors can edit / delete own comments
 * - staff can hide / unhide / delete
 * - pagination via "Показать ещё"
 */
const PAGE_SIZE = 10;

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function Avatar({ src, name, size = 36 }) {
  const initial = (name || "?").trim().slice(0, 1).toUpperCase();
  return (
    <div
      className="rounded-full bg-sand/50 overflow-hidden border hairline flex items-center justify-center text-espresso/60 font-serif shrink-0"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {src ? (
        <img src={src} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      ) : (
        <UserIcon className="w-4 h-4" />
      )}
    </div>
  );
}

export default function MaterialDiscussion({ material }) {
  const { user } = useAuth();
  const materialId = material?.id;
  const [threads, setThreads] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error403, setError403] = useState(false);

  const isStaff = user?.role === "admin" || user?.role === "editor";

  const loadPage = useCallback(async (off = 0, append = false) => {
    if (!materialId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/materials/${materialId}/comments`, { params: { offset: off, limit: PAGE_SIZE } });
      setTotal(data.total);
      setOffset(off + (data.threads?.length || 0));
      setThreads((prev) => (append ? [...prev, ...data.threads] : data.threads));
      setError403(false);
    } catch (e) {
      if (e?.response?.status === 403) setError403(true);
      else toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [materialId]);

  const loadCount = useCallback(async () => {
    if (!materialId) return;
    try {
      const { data } = await api.get(`/materials/${materialId}/comments/count`);
      setCount(data.count || 0);
    } catch { /* noop */ }
  }, [materialId]);

  useEffect(() => {
    // Public: everyone (including anonymous) can read the discussion.
    loadPage(0, false);
    loadCount();
  }, [user, materialId, loadPage, loadCount]);

  if (!user) {
    return (
      <section className="mt-10" data-testid="discussion-section">
        <SectionHeader count={count} />
        {/* Compose block replaced by a lightweight sign-in prompt. */}
        <div className="rounded-2xl bg-white border hairline p-3 text-sm text-espresso flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between" data-testid="discussion-anon">
          <span>Войдите, чтобы оставить комментарий.</span>
          <div className="flex gap-2">
            <a href="/login" className="btn-pill min-h-[36px] px-4 inline-flex items-center bg-[#F2EADF] hover:bg-[#EADFCF] text-espresso border border-espresso/25" data-testid="discussion-anon-login">Войти</a>
            <a href="/register" className="btn-pill min-h-[36px] px-4 inline-flex items-center bg-[#D9E4C8] hover:bg-[#CFDCBB] text-[#3B4A2A] border border-[#B8C7A0]" data-testid="discussion-anon-register">Создать аккаунт</a>
          </div>
        </div>

        <div className="mt-3 space-y-2.5" data-testid="discussion-threads">
          {loading && threads.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6 font-serif italic">Загрузка…</p>
          )}
          {!loading && threads.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6 font-serif italic" data-testid="discussion-empty">
              Пока нет комментариев.
            </p>
          )}
          {threads.map((t) => (
            <Thread key={t.comment.id} thread={t} isStaff={false} currentUser={null} onChanged={() => {}} />
          ))}
        </div>

        {offset < total && (
          <div className="mt-3 text-center">
            <Button variant="outline" className="btn-pill min-h-[36px]" onClick={() => loadPage(offset, true)} disabled={loading}>
              {loading ? "Загрузка…" : "Показать ещё"}
            </Button>
          </div>
        )}
      </section>
    );
  }

  const publish = async (parentId = null) => {
    const t = body.trim();
    if (!t) return;
    setSending(true);
    try {
      await api.post(`/materials/${materialId}/comments`, { body: t, parent_comment_id: parentId });
      setBody("");
      // Reload from scratch (comment count changed; new top-level appears at end after existing pages).
      await loadPage(0, false);
      await loadCount();
      toast.success("Комментарий опубликован");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  if (error403) {
    return (
      <section className="mt-10" data-testid="discussion-section">
        <SectionHeader count={count} />
        <div className="rounded-2xl bg-white border hairline p-4 text-sm text-muted-foreground">
          Обсуждение недоступно.
        </div>
      </section>
    );
  }

  return (
    <section className="mt-10" data-testid="discussion-section">
      <SectionHeader count={count} />

      {/* Publish form */}
      <div className="rounded-2xl bg-white border hairline p-4" data-testid="discussion-form">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          placeholder="Напишите комментарий или задайте вопрос по материалу…"
          className="w-full rounded-xl border border-input bg-white px-3 py-2 text-sm font-serif text-espresso focus:outline-none focus:ring-2 focus:ring-espresso/20"
          data-testid="discussion-input"
          disabled={sending}
        />
        <div className="mt-2 flex justify-end">
          <Button
            onClick={() => publish(null)}
            disabled={sending || !body.trim()}
            className="btn-pill min-h-[40px] bg-[#D9E4C8] hover:bg-[#CFDCBB] text-[#3B4A2A] border border-[#B8C7A0] shadow-none font-medium"
            data-testid="discussion-publish"
          >
            {sending ? "Публикуем…" : "Опубликовать"}
          </Button>
        </div>
      </div>

      {/* Threads */}
      <div className="mt-3 space-y-2.5" data-testid="discussion-threads">
        {loading && threads.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6 font-serif italic">Загрузка…</p>
        )}
        {!loading && threads.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6 font-serif italic" data-testid="discussion-empty">
            Пока нет комментариев. Будьте первым!
          </p>
        )}
        {threads.map((t) => (
          <Thread
            key={t.comment.id}
            thread={t}
            isStaff={isStaff}
            currentUser={user}
            onChanged={() => { loadPage(0, false); loadCount(); }}
          />
        ))}
      </div>

      {offset < total && (
        <div className="mt-3 text-center">
          <Button
            variant="outline"
            className="btn-pill min-h-[36px]"
            onClick={() => loadPage(offset, true)}
            disabled={loading}
            data-testid="discussion-load-more"
          >
            {loading ? "Загрузка…" : "Показать ещё"}
          </Button>
        </div>
      )}
    </section>
  );
}

function SectionHeader({ count }) {
  return (
    <div className="flex items-baseline gap-2 mb-4">
      <h2 className="font-serif text-2xl text-espresso">Обсуждение</h2>
      <span className="text-xs text-muted-foreground" data-testid="discussion-count">· {count}</span>
    </div>
  );
}

function CommentView({ comment, currentUser, isStaff, onChanged, onReply, canReply, compact = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const isOwn = currentUser?.id === comment.user_id;
  const isDeleted = comment.status === "deleted";
  const isHidden = comment.status === "hidden";

  const save = async () => {
    const t = draft.trim();
    if (!t) return;
    setSaving(true);
    try {
      await api.patch(`/comments/${comment.id}`, { body: t });
      setEditing(false);
      toast.success("Комментарий обновлён");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const doDelete = async () => {
    const isTop = !comment.parent_comment_id;
    const confirmText = isTop
      ? "Удалить комментарий и все ответы на него?"
      : "Удалить комментарий?";
    if (!window.confirm(confirmText)) return;
    setBusy(true);
    try {
      await api.delete(`/comments/${comment.id}`);
      toast.success("Комментарий удалён");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const staffHide = async () => {
    setBusy(true);
    try {
      await api.post(`/staff/comments/${comment.id}/hide`);
      toast.success("Скрыто");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const staffUnhide = async () => {
    setBusy(true);
    try {
      await api.post(`/staff/comments/${comment.id}/unhide`);
      toast.success("Показано");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const staffDelete = async () => {
    const isTop = !comment.parent_comment_id;
    const confirmText = isTop
      ? "Удалить комментарий и все ответы на него?"
      : "Удалить комментарий?";
    if (!window.confirm(confirmText)) return;
    setBusy(true);
    try {
      await api.delete(`/staff/comments/${comment.id}`);
      toast.success("Удалено");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div id={`comment-${comment.id}`} className={`flex gap-2.5 ${isHidden ? "opacity-60" : ""}`} data-testid={`comment-${comment.id}`}>
      <Avatar src={comment.author_avatar} name={comment.author_name} size={compact ? 28 : 32} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap leading-tight">
          <span className="text-sm font-medium text-espresso" data-testid={`comment-${comment.id}-author`}>
            {comment.author_name || (isDeleted ? "—" : "Пользователь")}
          </span>
          {comment.author_is_staff && !isDeleted && (
            <span className="text-[10px] uppercase tracking-widest text-espresso/70" data-testid={`comment-${comment.id}-team-label`}>
              Команда Anokina Studio
            </span>
          )}
          <span className="text-[11px] text-muted-foreground">{fmtDate(comment.created_at)}</span>
          {comment.edited && !isDeleted && (
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground/80">изменено</span>
          )}
          {isHidden && <span className="text-[10px] uppercase tracking-widest text-[#8A4D14]">скрыто</span>}
        </div>
        {editing ? (
          <div className="mt-1.5">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              className="w-full rounded-xl border border-input bg-white px-3 py-2 text-sm font-serif text-espresso focus:outline-none focus:ring-2 focus:ring-espresso/20"
              disabled={saving}
              data-testid={`comment-${comment.id}-edit-input`}
            />
            <div className="mt-2 flex gap-2 justify-end">
              <Button variant="outline" className="btn-pill min-h-[32px] text-xs" disabled={saving} onClick={() => { setEditing(false); setDraft(comment.body); }}>
                Отмена
              </Button>
              <Button
                className="btn-pill min-h-[32px] text-xs bg-[#D9E4C8] hover:bg-[#CFDCBB] text-[#3B4A2A] border border-[#B8C7A0] shadow-none"
                onClick={save}
                disabled={saving || !draft.trim()}
                data-testid={`comment-${comment.id}-edit-save`}
              >
                {saving ? "Сохраняем…" : "Сохранить"}
              </Button>
            </div>
          </div>
        ) : (
          <div className={`mt-0.5 font-serif whitespace-pre-wrap leading-snug text-[15px] ${isDeleted ? "text-muted-foreground italic" : "text-espresso"}`}>
            {comment.body}
          </div>
        )}
        {!isDeleted && !editing && (
          <div className="mt-1 flex gap-3 flex-wrap text-[11px] uppercase tracking-widest">
            {canReply && (
              <button onClick={onReply} className="text-espresso/70 hover:text-espresso" data-testid={`comment-${comment.id}-reply-btn`}>
                Ответить
              </button>
            )}
            {isOwn && (
              <>
                <button onClick={() => setEditing(true)} className="text-espresso/70 hover:text-espresso" data-testid={`comment-${comment.id}-edit-btn`}>
                  Редактировать
                </button>
                <button onClick={doDelete} disabled={busy} className="text-muted-foreground hover:text-red-500 disabled:opacity-50" data-testid={`comment-${comment.id}-delete-btn`}>
                  Удалить
                </button>
              </>
            )}
            {isStaff && !isOwn && (
              <button onClick={staffDelete} disabled={busy} className="text-muted-foreground hover:text-red-500 disabled:opacity-50" data-testid={`comment-${comment.id}-staff-delete-btn`}>
                Удалить
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Thread({ thread, isStaff, currentUser, onChanged }) {
  const [replying, setReplying] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);
  const parentId = thread.comment.id;
  const isDeleted = thread.comment.status === "deleted";

  const sendReply = async () => {
    const t = replyBody.trim();
    if (!t) return;
    setSending(true);
    try {
      await api.post(`/materials/${thread.comment.material_id}/comments`, { body: t, parent_comment_id: parentId });
      setReplyBody("");
      setReplying(false);
      onChanged?.();
      toast.success("Ответ опубликован");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="rounded-xl bg-[#FBF6ED] border hairline px-3 py-2.5 sm:px-4 sm:py-3" data-testid={`thread-${parentId}`}>
      <CommentView
        comment={thread.comment}
        currentUser={currentUser}
        isStaff={isStaff}
        onChanged={onChanged}
        onReply={() => setReplying((v) => !v)}
        canReply={!isDeleted}
      />
      {(thread.replies?.length > 0 || replying) && (
        <div className="mt-2.5 pl-4 sm:pl-6 border-l hairline space-y-2">
          {thread.replies.map((r) => (
            <CommentView
              key={r.id}
              comment={r}
              currentUser={currentUser}
              isStaff={isStaff}
              onChanged={onChanged}
              canReply={false}
              compact
            />
          ))}
          {replying && (
            <div className="rounded-xl bg-white border hairline p-2.5">
              <textarea
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                rows={2}
                placeholder="Ваш ответ…"
                className="w-full rounded-lg border border-input bg-white px-3 py-2 text-sm font-serif text-espresso focus:outline-none focus:ring-2 focus:ring-espresso/20"
                data-testid={`thread-${parentId}-reply-input`}
                disabled={sending}
              />
              <div className="mt-2 flex gap-2 justify-end">
                <Button variant="outline" className="btn-pill min-h-[32px] text-xs" onClick={() => { setReplying(false); setReplyBody(""); }} disabled={sending}>
                  Отмена
                </Button>
                <Button
                  className="btn-pill min-h-[32px] text-xs bg-[#D9E4C8] hover:bg-[#CFDCBB] text-[#3B4A2A] border border-[#B8C7A0] shadow-none"
                  onClick={sendReply}
                  disabled={sending || !replyBody.trim()}
                  data-testid={`thread-${parentId}-reply-send`}
                >
                  {sending ? "Отправляем…" : "Опубликовать"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
